import { GUARDIAN_SEARCH_DEFAULT_PAGE_SIZE, GUARDIAN_SEARCH_MAX_PAGE_SIZE } from "./../constants/CommunityConstants";
import { create } from "zustand";
import { getErrorFromResponse, jqXHR, ResponseError } from "../types/Api";
import {
  BaseGuardian,
  Guardian,
  PurgeScope,
  Student,
  RestrictionType,
  Restriction,
  getRestrictionListValue,
  isRestrictionForStudent,
} from "../types/Community";
import Api from "../utils/Api";
import { useSessionStore } from "./SessionStore";
import FileSaver from "file-saver";

export const userDetailsURL = (qoriaUserId: string): string => {
  const device = useSessionStore.getState().getDevice();
  return `/cadm/v2/appliances/${device.id}/students/${qoriaUserId}`;
};

export const userGuardiansURL = (qoriaUserId: string): string => {
  const device = useSessionStore.getState().getDevice();
  return `/cadm/v2/appliances/${device.id}/students/${qoriaUserId}/guardians`;
};

export const connectionsURL = (qoriaUserId: string): string => {
  const device = useSessionStore.getState().getDevice();
  return `/cadm/v2/appliances/${device.id}/students/${qoriaUserId}/claims`;
};

const userDetailsURLv1 = (stableId: string): string => {
  const device = useSessionStore.getState().getDevice();
  return `/cadm/v1/appliances/${device.id}/students/${stableId}`;
};

const userGuardiansURLv1 = (stableId: string): string => {
  const device = useSessionStore.getState().getDevice();
  return `/cadm/v1/appliances/${device.id}/students/${stableId}/guardians`;
};

const connectionsURLv1 = (stableId: string): string => {
  const device = useSessionStore.getState().getDevice();
  return `/cadm/v1/appliances/${device.id}/students/${stableId}/claims`;
};

export type StudentIdent = Pick<Student, "qoriaUserId" | "id">;

export const exportGuardiansURL = (params: URLSearchParams): string => {
  const device = useSessionStore.getState().getDevice();
  if (params && params.toString().length > 0) {
    const paramString = params.toString();
    return `/cadm/v2/appliances/${device.id}/guardians/export?${paramString}`;
  }
  return `/cadm/v2/appliances/${device.id}/guardians/export`;
};

export const getExportGuardianParams = (filter: FilterGuardianOptions): URLSearchParams => {
  const urlSearchParams = new URLSearchParams();
  if (filter.term) {
    urlSearchParams.append("searchText", filter.term);
  }
  if (filter.studentStatus) {
    urlSearchParams.append("studentFilter", filter.studentStatus);
  }
  if (filter.groupId) {
    urlSearchParams.append("groupId", filter.groupId);
  }
  return urlSearchParams;
};

export const guardianFetchURL = (): string => {
  const device = useSessionStore.getState().getDevice();
  return `/cadm/v1/appliances/${device.id}/guardians`;
};

export const guardianSearchURL = (): string => {
  const device = useSessionStore.getState().getDevice();
  return `/cadm/v1/appliances/${device.id}/guardians/search`;
};

export const guardianPurgeURL = (): string => {
  const device = useSessionStore.getState().getDevice();
  return `/cadm/v1/appliances/${device.id}/guardians/purge`;
};

export const guardiansURL = (): string => {
  const device = useSessionStore.getState().getDevice();
  return `/cadm/v1/appliances/${device.id}/guardians`;
};

export const guardianDetailsURL = (guardianIdOrEmail: string): string => {
  const device = useSessionStore.getState().getDevice();
  return `/cadm/v1/appliances/${device.id}/guardians/${encodeURIComponent(guardianIdOrEmail)}`;
};

export const restrictionsURL = (guardianIdOrEmail: string): string => {
  const device = useSessionStore.getState().getDevice();
  return `/cadm/v1/appliances/${device.id}/guardians/${encodeURIComponent(guardianIdOrEmail)}/restriction`;
};

const getRestrictedStudentsCount = (guardian: Guardian, students: Student[]): number => {
  if (guardian.restriction?.type === RestrictionType.GuardianClaimGlobalRestriction) {
    return students.length;
  } else if (guardian.restriction?.type === RestrictionType.GuardianClaimUserListRestriction) {
    const { userListValues } = guardian.restriction;
    const restricted = students.filter((s) => userListValues.some((r) => isRestrictionForStudent(r, s)));
    return restricted.length;
  }
  return 0;
};

const getUnclaimedStudentsCount = (guardian: Guardian, students: Student[]): number => {
  if (guardian.restriction?.type === RestrictionType.GuardianClaimGlobalRestriction) {
    return 0;
  } else if (guardian.restriction?.type === RestrictionType.GuardianClaimUserListRestriction) {
    const { userListValues } = guardian.restriction;
    const filtered_students = students.filter((s) => !s.claimed && !userListValues.some((r) => isRestrictionForStudent(r, s)));
    return filtered_students.length;
  }
  return students.filter((s) => !s.claimed).length;
};

const countGuardianUsers = (guardian: Guardian) => {
  const updated: Guardian = { ...guardian };
  if (guardian.students?.length) {
    updated.totalCount = guardian.students.length;
    updated.claimCount = guardian.students?.filter((s) => s.claimed).length;
    updated.delegatedCount = guardian.students?.filter((s) => s.delegated).length;
    updated.restrictedCount = getRestrictedStudentsCount(guardian, guardian.students);
    updated.linkCount = getUnclaimedStudentsCount(guardian, guardian.students);
  }
  return updated;
};

export interface SearchOptions {
  term?: string;
  page?: number;
  details?: boolean;
  pageSize?: number;
}

export interface FilterGuardianOptions {
  term?: string;
  studentStatus?: string;
  groupId?: string;
  page?: number;
  details?: boolean;
  pageSize?: number;
  cursor?: string;
}

// Helper function to check if any filter is applied
export const hasFilter = (filter: FilterGuardianOptions): boolean => {
  return !!filter?.studentStatus || !!filter?.groupId || !!filter?.term;
};

export interface PurgeOptions {
  scope: PurgeScope;
}

export interface GuardianStore {
  guardians: Guardian[];
  hasNextPage: boolean;
  search: (options: SearchOptions) => Promise<Guardian[]>;
  fetchGuardians: (options: FilterGuardianOptions) => Promise<Guardian[]>;
  downloadAllGuardians: (filter: FilterGuardianOptions) => Promise<void>;
  fetchGuardianDetails: (guardianId: string) => Promise<Guardian>;
  fetchUserGuardians: (student: StudentIdent) => Promise<Guardian[]>;
  addUserGuardian: (student: StudentIdent, guardian: BaseGuardian) => Promise<Guardian>;
  removeUserGuardian: (student: StudentIdent, guardian: Guardian) => Promise<Guardian>;
  removeClaim: (student: StudentIdent, guardian: Guardian) => Promise<Guardian>;
  restrictStudent: (student: Student, guardian: Guardian) => Promise<Guardian>;
  unRestrictStudent: (student: Student, guardian: Guardian) => Promise<Guardian>;
  updateGuardian: (guardianId: string, guardian: Guardian) => Promise<Guardian>;
  deleteGuardian: (guardian: Guardian) => Promise<void>;
  purgeGuardians: (options: PurgeOptions) => Promise<void>;
  setGuardian: (guardian: Guardian) => void;
  reset: () => void;
}

export const useGuardianStore = create<GuardianStore>((set, get) => ({
  guardians: [],
  hasNextPage: true,

  search: async (options: SearchOptions): Promise<Guardian[]> => {
    // TODO: deprecated, remove this!
    const pageNumber = options.page ?? 0;
    const pageSize = options.pageSize ? Math.min(options.pageSize, GUARDIAN_SEARCH_MAX_PAGE_SIZE) : GUARDIAN_SEARCH_DEFAULT_PAGE_SIZE;

    const params = new URLSearchParams({ page: String(pageNumber), limit: String(pageSize) });

    if (options.term) {
      params.append("q", options.term);
    }

    let guardians: Guardian[] = [],
      hasNextPage = true,
      statusCode = 200;

    // eslint-disable-next-line
    await Api.getAsync(`${guardianSearchURL()}?${params.toString()}`).then(
      (response: { guardians: Guardian[] }) => {
        hasNextPage = pageSize === response.guardians.length;

        if (pageNumber > 0) {
          guardians = [...get().guardians, ...response.guardians];
        } else {
          guardians = response.guardians;
        }
      },
      (reason: jqXHR) => (statusCode = reason?.status)
    );

    if (statusCode !== 200) {
      throw new ResponseError("Failed to fetch guardians", statusCode);
    }

    set({ guardians, hasNextPage });

    return guardians;
  },

  fetchGuardians: async (options: FilterGuardianOptions): Promise<Guardian[]> => {
    const cursor = options.cursor ?? "";
    const pageSize = options.pageSize ? Math.min(options.pageSize, GUARDIAN_SEARCH_MAX_PAGE_SIZE) : GUARDIAN_SEARCH_DEFAULT_PAGE_SIZE;

    const params = new URLSearchParams({ cursor: String(cursor), limit: String(pageSize) });

    if (options.term && options.term.length > 2) {
      params.append("searchText", options.term);
    }

    if (options.studentStatus) {
      params.append("studentFilter", options.studentStatus);
    }

    if (options.groupId) {
      params.append("groupId", options.groupId);
    }

    let guardians: Guardian[] = [],
      hasNextPage = true,
      statusCode = 200;

    // eslint-disable-next-line
    await Api.getAsync(`${guardianFetchURL()}?${params.toString()}`).then(
      (response: { guardians: Guardian[] }) => {
        hasNextPage = pageSize === response.guardians.length;

        if (cursor !== "") {
          guardians = [...get().guardians, ...response.guardians];
        } else {
          guardians = response.guardians;
        }
      },
      (reason: jqXHR) => (statusCode = reason?.status)
    );

    if (statusCode !== 200) {
      throw new ResponseError("Failed to fetch guardians", statusCode);
    }

    set({ guardians, hasNextPage });

    return guardians;
  },

  downloadAllGuardians: async (filter: FilterGuardianOptions): Promise<void> => {
    const device = useSessionStore.getState().getDevice();
    const urlSearchParams = getExportGuardianParams(filter);

    const response = await fetch(exportGuardiansURL(urlSearchParams), {
      headers: {
        Accept: "text/csv",
        Token: useSessionStore.getState().getToken(),
      },
    });

    if (!response.ok) {
      throw new ResponseError("Failed to export parent data", response.status);
    }

    FileSaver.saveAs(await response.blob(), `${device.id}_parent_data.csv`);
  },

  fetchUserGuardians: async (student: StudentIdent): Promise<Guardian[]> => {
    let guardians: Guardian[] = [],
      statusCode = 200;

    const url = student.qoriaUserId ? userDetailsURL(student.qoriaUserId) : userDetailsURLv1(student.id);
    await Api.getAsync(url).then(
      (response: { guardians: Guardian[] }) => {
        guardians = response.guardians;
      },
      (reason: jqXHR) => (statusCode = reason?.status)
    );

    if (statusCode !== 200) {
      throw new ResponseError("Failed to fetch parents", statusCode);
    }

    return guardians;
  },

  fetchGuardianDetails: async (guardianId: string): Promise<Guardian> => {
    let guardian: Guardian | undefined,
      statusCode = 200;

    await Api.getAsync(guardianDetailsURL(guardianId)).then(
      (response: { guardian: Guardian; students: Student[] }) => {
        guardian = response.guardian;
        guardian.students = response.students;
      },
      (reason: jqXHR) => (statusCode = reason?.status)
    );

    if (!guardian || statusCode !== 200) {
      throw new ResponseError("Failed to fetch parent", statusCode);
    }

    return guardian;
  },

  addUserGuardian: async (student: StudentIdent, guardian: BaseGuardian): Promise<Guardian> => {
    let saved: Guardian | undefined,
      errorMessage: string | undefined,
      statusCode = 200;

    const url = student.qoriaUserId ? userGuardiansURL(student.qoriaUserId) : userGuardiansURLv1(student.id);
    await Api.postAsync(url, { guardians: [guardian] }).then(
      (response: { guardians: Guardian[] }) => {
        saved = response.guardians[0];
        saved.claimed = false;
        saved.delegated = false;
      },
      (reason: jqXHR) => {
        statusCode = reason?.status;
        errorMessage = getErrorFromResponse(reason);
      }
    );

    if (!saved || statusCode !== 200) {
      if (Math.floor(statusCode / 100) === 4 && errorMessage) {
        throw new ResponseError(`Failed to add parent: ${errorMessage}`, statusCode);
      }
      throw new ResponseError("Failed to add parent", statusCode);
    }

    return saved;
  },

  removeUserGuardian: async (student: StudentIdent, guardian: Guardian): Promise<Guardian> => {
    let saved: Guardian | undefined,
      statusCode = 204;

    const url = student.qoriaUserId ? userGuardiansURL(student.qoriaUserId) : userGuardiansURLv1(student.id);
    await Api.deleteAsync(url, { guardians: [guardian] }).then(
      () => {
        saved = { ...guardian };

        if (guardian.students?.length) {
          saved.students = guardian.students.filter((s) =>
            student.qoriaUserId ? s.qoriaUserId !== student.qoriaUserId : s.id !== student.id
          );
          saved.linkCount = saved.students.length;
          saved.claimCount = saved.students?.filter((s) => s.claimed).length;
          saved.delegatedCount = saved.students?.filter((s) => s.delegated).length;
        }
      },
      (reason: jqXHR) => (statusCode = reason?.status)
    );

    if (!saved || statusCode !== 204) {
      throw new ResponseError("Failed to remove parent", statusCode);
    }

    return saved;
  },

  restrictStudent: async (student: Student, guardian: Guardian): Promise<Guardian> => {
    // used to restrict an unclaimed student (use removeClaim for claimed students)
    let saved: Guardian | undefined;

    if (!guardian.restriction) {
      let statusCode = 200;
      await Api.postAsync(restrictionsURL(guardian.email), {
        type: RestrictionType.GuardianClaimUserListRestriction,
        userListValues: [getRestrictionListValue(student)],
      }).then(
        (response: Restriction) => {
          saved = { ...guardian };
          saved.claimed = false;
          saved.delegated = false;
          saved.restriction = response;
          saved = countGuardianUsers(saved);
        },
        (reason: jqXHR) => (statusCode = reason?.status)
      );

      if (!saved || statusCode !== 200) {
        throw new ResponseError("Failed to restrict student", statusCode);
      }

      return saved;
    } else {
      let statusCode = 200;
      const userListValues = [...guardian.restriction.userListValues, getRestrictionListValue(student)];
      await Api.putAsync(restrictionsURL(guardian.email), {
        type: RestrictionType.GuardianClaimUserListRestriction,
        userListValues: userListValues,
      }).then(
        (response: Restriction) => {
          saved = { ...guardian };
          saved.claimed = false;
          saved.delegated = false;
          saved.restriction = response;
          saved = countGuardianUsers(saved);
        },
        (reason: jqXHR) => (statusCode = reason?.status)
      );

      if (!saved || statusCode !== 200) {
        throw new ResponseError("Failed to restrict student", statusCode);
      }

      return saved;
    }
  },

  unRestrictStudent: async (student: Student, guardian: Guardian): Promise<Guardian> => {
    let saved: Guardian | undefined,
      statusCode = 204;

    const userListValues = guardian.restriction?.userListValues.filter((item) => !isRestrictionForStudent(item, student));

    if (userListValues?.length === 0) {
      await Api.deleteAsync(restrictionsURL(guardian.email)).then(
        () => {
          saved = { ...guardian };
          saved.claimed = false;
          saved.delegated = false;
          saved.restriction = undefined;
          saved = countGuardianUsers(saved);
        },
        (reason: jqXHR) => (statusCode = reason?.status)
      );
    } else {
      await Api.putAsync(restrictionsURL(guardian.email), {
        type: RestrictionType.GuardianClaimUserListRestriction,
        userListValues: userListValues,
      }).then(
        (response: Restriction) => {
          saved = { ...guardian };
          saved.claimed = false;
          saved.delegated = false;
          saved.restriction = response;
          saved = countGuardianUsers(saved);
        },
        (reason: jqXHR) => (statusCode = reason?.status)
      );
    }

    if (!saved || (statusCode !== 200 && statusCode !== 204)) {
      throw new ResponseError("Failed to unrestrict student", statusCode);
    }

    return saved;
  },

  removeClaim: async (student: StudentIdent, guardian: Guardian): Promise<Guardian> => {
    let saved: Guardian | undefined,
      statusCode = 204;

    const url = student.qoriaUserId ? connectionsURL(student.qoriaUserId) : connectionsURLv1(student.id);
    await Api.deleteAsync(url, { guardians: [guardian] }).then(
      // removing the claim will also add a restriction for this student
      () => {
        saved = { ...guardian };

        const matchesStudent = (s: Student) => (student.qoriaUserId && s.qoriaUserId === student.qoriaUserId) || s.id === student.id;

        let user: Student | undefined;
        if (guardian.students?.length) {
          user = guardian.students.find(matchesStudent);
        }

        if (guardian.restriction) {
          if (guardian.restriction.type !== RestrictionType.GuardianClaimGlobalRestriction && user) {
            saved.restriction = {
              ...guardian.restriction,
              userListValues: [...guardian.restriction.userListValues, getRestrictionListValue(user)],
            };
          }
        } else {
          if (user) {
            saved.restriction = {
              type: RestrictionType.GuardianClaimUserListRestriction,
              userListValues: [getRestrictionListValue(user)],
            };
          }
        }

        saved.claimed = false;
        saved.delegated = false;

        if (guardian.students?.length) {
          saved.students = guardian.students.map((s) => {
            if (matchesStudent(s)) {
              return { ...s, claimed: false, delegated: false };
            }
            return s;
          });
          saved = countGuardianUsers(saved);
        }
      },
      (reason: jqXHR) => (statusCode = reason?.status)
    );

    if (!saved || statusCode !== 204) {
      throw new ResponseError("Failed to disconnect parent", statusCode);
    }

    return saved;
  },

  updateGuardian: async (guardianId: string, guardian: Guardian): Promise<Guardian> => {
    let saved: Guardian | undefined,
      errorMessage: string | undefined,
      statusCode = 200;

    await Api.putAsync(guardianDetailsURL(guardianId), guardian).then(
      (response: Guardian) => {
        saved = response;
        // copy over fields not modified/returned
        saved.totalCount = guardian.students?.length;
        saved.claimCount = guardian.students?.filter((s) => s.claimed).length;
        saved.delegatedCount = guardian.students?.filter((s) => s.delegated).length;
        saved.restrictedCount = getRestrictedStudentsCount(guardian, guardian.students || []);
        saved.linkCount = getUnclaimedStudentsCount(guardian, guardian.students || []);
        saved.claimed = guardian.claimed;
        saved.delegated = guardian.delegated;
        saved.restriction = guardian.restriction;
      },
      (reason: jqXHR) => {
        statusCode = reason?.status;
        errorMessage = getErrorFromResponse(reason);
      }
    );

    if (!saved || statusCode !== 200) {
      if (Math.floor(statusCode / 100) === 4 && errorMessage) {
        throw new ResponseError(`Failed to update parent: ${errorMessage}`, statusCode);
      }
      throw new ResponseError("Failed to update parent", statusCode);
    }

    return saved;
  },

  deleteGuardian: async (guardian: Guardian): Promise<void> => {
    let errorMessage: string | undefined,
      statusCode = 204;

    await Api.deleteAsync(`${guardiansURL()}`, { guardians: [guardian] }).then(
      () => ({}),
      (reason: jqXHR) => {
        statusCode = reason?.status;
        errorMessage = getErrorFromResponse(reason);
      }
    );

    if (statusCode !== 204) {
      if (Math.floor(statusCode / 100) === 4 && errorMessage) {
        throw new ResponseError(`Failed to delete parent: ${errorMessage}`, statusCode);
      }
      throw new ResponseError("Failed to delete parent", statusCode);
    }

    set({ guardians: get().guardians.filter((g) => g.id !== guardian.id) });
  },

  purgeGuardians: async (options: PurgeOptions): Promise<void> => {
    let errorMessage: string | undefined,
      statusCode = 204;

    const params = new URLSearchParams({ scope: options.scope });

    await Api.deleteAsync(`${guardianPurgeURL()}?${params.toString()}`).then(
      () => ({}),
      (reason: jqXHR) => {
        statusCode = reason?.status;
        errorMessage = getErrorFromResponse(reason);
      }
    );

    if (statusCode !== 204) {
      if (Math.floor(statusCode / 100) === 4 && errorMessage) {
        throw new ResponseError(`Failed to purge parents: ${errorMessage}`, statusCode);
      }
      throw new ResponseError("Failed to purge parents", statusCode);
    }
  },

  setGuardian: (guardian: Guardian) => {
    const guardians: Guardian[] = [];

    for (const g of get().guardians) {
      if (g.id === guardian.id) {
        guardians.push(guardian);
      } else {
        guardians.push(g);
      }
    }

    set({ guardians });
  },

  reset: () => {
    set({ guardians: [], hasNextPage: true });
  },
}));
